import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';
import { useUserTimezone } from '../../../hooks/useUserTimezone';

// ========================================
// Types
// ========================================

interface InvoiceLineItem {
  id: number;
  line: number;
  externalItemName: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  creditId: number | null;
  debitId: number | null;
}

interface ExternalInvoice {
  id: number;
  internalId: string;
  externalId: string;
  tranId: string;
  tranDate: string;
  createdDate: string;
  lastModifiedDate: string;
  dueDate: string;
  memo: string;
  totalCostEstimate: number;
  estGrossProfit: number;
  estGrossProfitPercent: number;
  exchangeRate: number;
  currencyName: string;
  subTotal: number;
  discountTotal: number;
  taxTotal: number;
  total: number;
  source: string;
  status: string;
  email: string;
  pubId: string;
  createdAt: string;
  updatedAt: string;
  invoiceListItems: InvoiceLineItem[];
}

interface Publication {
  id: number;
  name: string;
  accountId: number;
}

interface TransactionCategory {
  id: string;
  name: string;
}

interface Account {
  id: number;
  name: string;
}

interface LineItemSelection {
  publicationId: string;
  categoryId: string;
}

interface ConfirmationState {
  isOpen: boolean;
  lineItemId: number | null;
  action: 'transfer' | 'cancelCredit' | null;
  message: string;
}

// ========================================
// Styled Components
// ========================================

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 400;
  color: #0891b2;
  margin: 0;
`;

const FetchButton = styled.button`
  padding: 10px 20px;
  background: #6b7280;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  
  &:hover {
    background: #4b5563;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 32px;
  margin-bottom: 32px;
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #111827;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${({ status }) => {
    const lower = status?.toLowerCase() || '';
    if (lower.includes('paid')) {
      return 'background: #d1fae5; color: #065f46;';
    } else if (lower.includes('open')) {
      return 'background: #fef3c7; color: #92400e;';
    } else if (lower.includes('void')) {
      return 'background: #fee2e2; color: #991b1b;';
    }
    return 'background: #e5e7eb; color: #374151;';
  }}
`;

const AccountSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  margin-bottom: 24px;
  
  &:focus {
    outline: none;
    border-color: #0891b2;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: #f9fafb;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  color: #111827;
  vertical-align: middle;
`;

const LineItemSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  background: white;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #0891b2;
  }
`;

const TransferButton = styled.button`
  padding: 6px 16px;
  background: #0891b2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    background: #0e7490;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelCreditButton = styled.button`
  padding: 6px 16px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    background: #dc2626;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionsCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const TransactionLink = styled(Link)`
  color: #0891b2;
  text-decoration: none;
  font-size: 13px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const TransactionInfo = styled.span`
  font-size: 13px;
  color: #374151;
`;

const LoadingState = styled.div`
  padding: 60px;
  text-align: center;
  color: #666;
`;

const Breadcrumb = styled.div`
  font-size: 14px;
  margin-bottom: 16px;
  
  a {
    color: #0891b2;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

// Confirmation Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  max-width: 400px;
  width: 90%;
`;

const ModalMessage = styled.p`
  font-size: 14px;
  color: #374151;
  margin: 0 0 20px 0;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  
  ${({ $variant }) => $variant === 'primary' 
    ? 'background: #10b981; color: white; &:hover { background: #059669; }'
    : 'background: #e5e7eb; color: #374151; &:hover { background: #d1d5db; }'
  }
`;

// ========================================
// Utility Functions
// ========================================

const formatMoney = (amount: number | null | undefined): string => {
  if (amount == null) return 'None';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatPercent = (value: number | null | undefined): string => {
  if (value == null) return 'None';
  return `${value.toFixed(0)}%`;
};

// ========================================
// Component
// ========================================

function AdminExternalInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { setTimezoneFromResponse, formatDateTime } = useUserTimezone();
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [invoice, setInvoice] = useState<ExternalInvoice | null>(null);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [categories, setCategories] = useState<TransactionCategory[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [lineItemSelections, setLineItemSelections] = useState<Record<number, LineItemSelection>>({});
  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    isOpen: false,
    lineItemId: null,
    action: null,
    message: ''
  });
  const [processing, setProcessing] = useState(false);

  const fetchInvoice = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/admin2/external_invoices/${id}.json`);
      setInvoice(response.data.externalInvoice);
      setPublications(response.data.publications || []);
      setCategories(response.data.transactionCategories || []);
      setAccounts(response.data.accounts || []);
      setTimezoneFromResponse(response.data);
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      toast.error('Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  }, [id, setTimezoneFromResponse]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  // Poll batch status to know when fetch is complete
  const pollBatch = useCallback((bid: string, callback: () => void) => {
    api.get(`/api/v1/batches/${bid}`)
      .then((response) => {
        if (response.data?.data?.attributes?.isComplete) {
          callback();
        } else {
          setTimeout(() => pollBatch(bid, callback), 1000);
        }
      })
      .catch(() => {
        // If batch status check fails, still try to refresh data
        callback();
      });
  }, []);

  const handleFetchInvoice = async () => {
    if (!invoice?.internalId) {
      toast.error('No NetSuite invoice ID available');
      return;
    }
    
    setFetching(true);
    try {
      // Use the same API endpoint as legacy FE with netsuite_invoice_id
      const response = await api.post('/api/v2/fetch_invoices', {
        netsuite_invoice_id: invoice.internalId
      });
      
      if (response.data?.data?.id) {
        toast.info('Fetching latest invoice info from NetSuite...');
        pollBatch(response.data.data.id, () => {
          fetchInvoice();
          setFetching(false);
          toast.success('Invoice updated successfully!');
        });
      } else {
        toast.error('Failed to start invoice fetch');
        setFetching(false);
      }
    } catch (error) {
      toast.error('Failed to fetch invoice from NetSuite');
      setFetching(false);
    }
  };

  const handleLineItemSelectionChange = (lineItemId: number, field: 'publicationId' | 'categoryId', value: string) => {
    setLineItemSelections(prev => ({
      ...prev,
      [lineItemId]: {
        ...prev[lineItemId],
        [field]: value
      }
    }));
  };

  // Filter publications by selected account
  const filteredPublications = selectedAccount
    ? publications.filter(pub => pub.accountId?.toString() === selectedAccount)
    : publications;

  const getLineItem = (lineItemId: number): InvoiceLineItem | undefined => {
    return invoice?.invoiceListItems.find(item => item.id === lineItemId);
  };

  const handleTransferClick = (lineItemId: number) => {
    const selection = lineItemSelections[lineItemId];
    if (!selection?.publicationId || !selection?.categoryId) {
      toast.warning('Please select both publication and category');
      return;
    }
    
    const lineItem = getLineItem(lineItemId);
    const publication = filteredPublications.find(p => p.id.toString() === selection.publicationId);
    
    setConfirmation({
      isOpen: true,
      lineItemId,
      action: 'transfer',
      message: `Transfer ${formatMoney(lineItem?.amount)} to ${publication?.name}?`
    });
  };

  const handleCancelCreditClick = (lineItemId: number) => {
    setConfirmation({
      isOpen: true,
      lineItemId,
      action: 'cancelCredit',
      message: 'Create debit to cancel out transferred funds? This action can not be undone!'
    });
  };

  const handleConfirmationCancel = () => {
    setConfirmation({
      isOpen: false,
      lineItemId: null,
      action: null,
      message: ''
    });
  };

  const handleConfirmationConfirm = async () => {
    if (!confirmation.lineItemId || !confirmation.action) return;
    
    setProcessing(true);
    
    try {
      if (confirmation.action === 'transfer') {
        await executeTransfer(confirmation.lineItemId);
      } else if (confirmation.action === 'cancelCredit') {
        await executeCancelCredit(confirmation.lineItemId);
      }
      
      // Refresh invoice data
      await fetchInvoice();
      toast.success(confirmation.action === 'transfer' ? 'Transfer completed' : 'Credit canceled');
    } catch (error) {
      console.error('Operation failed:', error);
      toast.error('Operation failed');
    } finally {
      setProcessing(false);
      handleConfirmationCancel();
    }
  };

  const executeTransfer = async (lineItemId: number) => {
    const selection = lineItemSelections[lineItemId];
    const lineItem = getLineItem(lineItemId);
    
    if (!selection || !lineItem) {
      throw new Error('Missing selection or line item');
    }
    
    // Step 1: Create Credit transaction
    const transactionResponse = await api.post('/api/v2/transactions', {
      transaction: {
        type: 'Credit',
        amount_in_dollars_and_cents: lineItem.amount,
        internal_note: '',
        public_note: '',
        brand_profile_id: selection.publicationId,
        category: selection.categoryId,
        internal_invoice_credit_memo_reference: '',
        completed: true,
        completed_at: new Date().toUTCString()
      }
    });
    
    const creditId = transactionResponse.data?.data?.id;
    if (!creditId) {
      throw new Error('Failed to create credit transaction');
    }
    
    // Step 2: Update line item with credit_id
    await api.patch(`/api/v2/invoice_list_items/${lineItemId}`, {
      invoice_list_item: {
        credit_id: creditId
      }
    });
  };

  const executeCancelCredit = async (lineItemId: number) => {
    const lineItem = getLineItem(lineItemId);
    
    if (!lineItem || !lineItem.creditId) {
      throw new Error('Missing line item or credit');
    }
    
    const selection = lineItemSelections[lineItemId];
    
    if (!selection?.publicationId || !selection?.categoryId) {
      throw new Error('Please select publication and category to cancel the credit');
    }
    
    // Step 1: Create Debit transaction to cancel out the credit
    const transactionResponse = await api.post('/api/v2/transactions', {
      transaction: {
        type: 'Debit',
        amount_in_dollars_and_cents: lineItem.amount,
        internal_note: '',
        public_note: '',
        brand_profile_id: selection.publicationId,
        category: selection.categoryId,
        internal_invoice_credit_memo_reference: '',
        completed: true,
        completed_at: new Date().toUTCString()
      }
    });
    
    const debitId = transactionResponse.data?.data?.id;
    if (!debitId) {
      throw new Error('Failed to create debit transaction');
    }
    
    // Step 2: Update line item with debit_id
    await api.patch(`/api/v2/invoice_list_items/${lineItemId}`, {
      invoice_list_item: {
        debit_id: debitId
      }
    });
  };

  const renderLineItemActions = (item: InvoiceLineItem) => {
    const selection = lineItemSelections[item.id];
    const hasCredit = item.creditId != null;
    const hasDebit = item.debitId != null;
    const hasSelection = selection?.publicationId && selection?.categoryId;
    
    // If both credit and debit exist, show the canceled out state
    if (hasCredit && hasDebit) {
      return (
        <TransactionInfo>
          <TransactionLink to={`/admin2/transactions/${item.creditId}`}>
            Credit #{item.creditId}
          </TransactionLink>
          {' canceled out with '}
          <TransactionLink to={`/admin2/transactions/${item.debitId}`}>
            Debit #{item.debitId}
          </TransactionLink>
        </TransactionInfo>
      );
    }
    
    // If only credit exists, show credit link and cancel button
    // Also show dropdowns if selection is empty (for page refresh scenario)
    if (hasCredit) {
      return (
        <ActionsCell>
          <ActionRow>
            <TransactionLink to={`/admin2/transactions/${item.creditId}`}>
              Credit #{item.creditId}
            </TransactionLink>
            <CancelCreditButton 
              onClick={() => handleCancelCreditClick(item.id)}
              disabled={!hasSelection}
              title={!hasSelection ? 'Please select publication and category first' : ''}
            >
              Cancel out credit
            </CancelCreditButton>
          </ActionRow>
          {!hasSelection && (
            <ActionRow>
              <LineItemSelect
                value={selection?.publicationId || ''}
                onChange={(e) => handleLineItemSelectionChange(item.id, 'publicationId', e.target.value)}
              >
                <option value="">Publication (required for cancel)</option>
                {filteredPublications.map(pub => (
                  <option key={pub.id} value={pub.id}>{pub.name}</option>
                ))}
              </LineItemSelect>
              <LineItemSelect
                value={selection?.categoryId || ''}
                onChange={(e) => handleLineItemSelectionChange(item.id, 'categoryId', e.target.value)}
              >
                <option value="">Category (required for cancel)</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </LineItemSelect>
            </ActionRow>
          )}
        </ActionsCell>
      );
    }
    
    // No credit yet, show transfer form
    return (
      <ActionRow>
        <LineItemSelect
          value={selection?.publicationId || ''}
          onChange={(e) => handleLineItemSelectionChange(item.id, 'publicationId', e.target.value)}
        >
          <option value="">Publication</option>
          {filteredPublications.map(pub => (
            <option key={pub.id} value={pub.id}>{pub.name}</option>
          ))}
        </LineItemSelect>
        <LineItemSelect
          value={selection?.categoryId || ''}
          onChange={(e) => handleLineItemSelectionChange(item.id, 'categoryId', e.target.value)}
        >
          <option value="">Category</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </LineItemSelect>
        <TransferButton
          onClick={() => handleTransferClick(item.id)}
          disabled={!selection?.publicationId || !selection?.categoryId}
        >
          Transfer
        </TransferButton>
      </ActionRow>
    );
  };

  if (loading) {
    return (
      <AdminLayout title="External Invoice" pageTitle="External invoices">
        <LoadingState>Loading invoice details...</LoadingState>
      </AdminLayout>
    );
  }

  if (!invoice) {
    return (
      <AdminLayout title="External Invoice" pageTitle="External invoices">
        <LoadingState>Invoice not found</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="External Invoice" pageTitle="External invoices">
      <Breadcrumb>
        <Link to="/admin2/external_invoices">External invoices</Link>
      </Breadcrumb>
      
      <PageHeader>
        <PageTitle>External Invoice {invoice.id}</PageTitle>
        <FetchButton onClick={handleFetchInvoice} disabled={fetching}>
          {fetching ? 'Fetching...' : 'Fetch most recent invoice info'}
        </FetchButton>
      </PageHeader>

      <SectionTitle>NetSuite invoice {invoice.tranId || invoice.internalId}</SectionTitle>
      
      <DetailsGrid>
        <DetailRow>
          <DetailLabel>Status</DetailLabel>
          <DetailValue>
            <StatusBadge status={invoice.status || ''}>
              {invoice.status || 'Unknown'}
            </StatusBadge>
          </DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Sub Total</DetailLabel>
          <DetailValue>{formatMoney(invoice.subTotal)}</DetailValue>
        </DetailRow>
        
        <DetailRow>
          <DetailLabel>Created date</DetailLabel>
          <DetailValue>{formatDateTime(invoice.createdDate)}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Total Cost Estimate</DetailLabel>
          <DetailValue>{invoice.totalCostEstimate != null ? formatMoney(invoice.totalCostEstimate) : 'None'}</DetailValue>
        </DetailRow>
        
        <DetailRow>
          <DetailLabel>Last modified date</DetailLabel>
          <DetailValue>{formatDateTime(invoice.lastModifiedDate)}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Estimated Gross Profit</DetailLabel>
          <DetailValue>
            {invoice.estGrossProfit != null 
              ? `${formatMoney(invoice.estGrossProfit)} (${formatPercent(invoice.estGrossProfitPercent)})`
              : 'None'
            }
          </DetailValue>
        </DetailRow>
        
        <DetailRow>
          <DetailLabel>NetSuite Transaction ID</DetailLabel>
          <DetailValue>{invoice.tranId || invoice.internalId || 'None'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Discount Total</DetailLabel>
          <DetailValue>{invoice.discountTotal != null ? formatMoney(invoice.discountTotal) : 'None'}</DetailValue>
        </DetailRow>
        
        <DetailRow>
          <DetailLabel>Due Date</DetailLabel>
          <DetailValue>{invoice.dueDate || 'None'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Tax Total</DetailLabel>
          <DetailValue>{invoice.taxTotal != null ? formatMoney(invoice.taxTotal) : 'None'}</DetailValue>
        </DetailRow>
        
        <DetailRow>
          <DetailLabel>Memo</DetailLabel>
          <DetailValue>{invoice.memo || 'None'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Total</DetailLabel>
          <DetailValue>{formatMoney(invoice.total)}</DetailValue>
        </DetailRow>
        
        <DetailRow>
          <DetailLabel>Email</DetailLabel>
          <DetailValue>{invoice.email || 'None'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Publication ID</DetailLabel>
          <DetailValue>{invoice.pubId || 'None'}</DetailValue>
        </DetailRow>
      </DetailsGrid>

      <AccountSelect 
        value={selectedAccount}
        onChange={(e) => setSelectedAccount(e.target.value)}
      >
        <option value="">Select Account to limit publication selectors</option>
        {accounts.map(account => (
          <option key={account.id} value={account.id}>{account.id} - {account.name}</option>
        ))}
      </AccountSelect>

      {invoice.invoiceListItems && invoice.invoiceListItems.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>Line</Th>
              <Th>Item</Th>
              <Th>Description</Th>
              <Th>Quantity/Rate</Th>
              <Th>Amount</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {invoice.invoiceListItems.map((item) => (
              <tr key={item.id}>
                <Td>{item.line}</Td>
                <Td>{item.externalItemName}</Td>
                <Td>{item.description || '-'}</Td>
                <Td>{item.quantity} x {formatMoney(item.rate)}</Td>
                <Td>{formatMoney(item.amount)}</Td>
                <Td>
                  <ActionsCell>
                    {renderLineItemActions(item)}
                  </ActionsCell>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Confirmation Modal */}
      {confirmation.isOpen && (
        <ModalOverlay onClick={handleConfirmationCancel}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalMessage>{confirmation.message}</ModalMessage>
            <ModalButtons>
              <ModalButton onClick={handleConfirmationCancel} disabled={processing}>
                Cancel
              </ModalButton>
              <span style={{ color: '#6b7280' }}>or</span>
              <ModalButton $variant="primary" onClick={handleConfirmationConfirm} disabled={processing}>
                {processing ? 'Processing...' : 'Confirm'}
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </AdminLayout>
  );
}

export default AdminExternalInvoiceDetailPage;
